import * as React from 'react'
import { cn } from '../../lib/utils'
import { Toast, type ToastProps } from './toast'

/**
 * Toaster — a Sonner-style stacked toaster, ported from the shipped DS2026 spec
 * (client `Toaster.tsx` + `toastStore.ts`). One uniform single-row toast per
 * item; collapsed they recede upward behind the newest, and hover/focus fans the
 * stack out. Auto-dismiss pauses while hovered/expanded; `loading` is sticky and
 * can morph into success/error in place (`toast.promise` / `toast.update`).
 *
 * Mount <Toaster /> once near the app root, then call the imperative `toast` API:
 *   toast.success('Saved')            toast.error('Failed')
 *   toast.loading('Uploading…')       toast.promise(p, { loading, success, error })
 *   toast('Deal created', { action: { label: 'View', onClick } })
 */

type ToastVariant = NonNullable<ToastProps['variant']>

export interface ToastItem {
  id: string
  variant: ToastVariant
  message: string
  /** ms until auto-dismiss; null = sticky (e.g. loading). */
  duration: number | null
  /** Inline text link (e.g. "View" / "Undo"). */
  action?: { label: string; onClick: () => void }
}

// ── Module store (useSyncExternalStore) ──────────────────────────────────────
let items: ToastItem[] = []
const listeners = new Set<() => void>()
let counter = 0

const subscribe = (l: () => void) => {
  listeners.add(l)
  return () => listeners.delete(l)
}
const getSnapshot = () => items
const emit = () => {
  items = items.slice()
  listeners.forEach((l) => l())
}

const DEFAULT_DURATION = 4000

function addToast(
  item: Omit<ToastItem, 'id' | 'duration'> & {
    id?: string
    duration?: number | null
  }
): string {
  const id = item.id ?? `fbui-toast-${++counter}`
  const next: ToastItem = {
    duration: item.variant === 'loading' ? null : DEFAULT_DURATION,
    ...item,
    id,
  }
  const existing = items.findIndex((t) => t.id === id)
  if (existing >= 0) {
    items = items.slice()
    items[existing] = next
  } else {
    items = [...items, next]
  }
  emit()
  return id
}

function updateToast(id: string, patch: Partial<Omit<ToastItem, 'id'>>) {
  const i = items.findIndex((t) => t.id === id)
  if (i < 0) return
  items = items.slice()
  items[i] = { ...items[i], ...patch }
  emit()
}

function dismissToast(id: string) {
  items = items.filter((t) => t.id !== id)
  emit()
}

type ToastOptions = {
  id?: string
  duration?: number | null
  action?: ToastItem['action']
}
const variantFn =
  (variant: ToastVariant) =>
  (message: string, opts: ToastOptions = {}) =>
    addToast({ variant, message, ...opts })

export const toast = Object.assign(
  (message: string, opts: ToastOptions & { variant?: ToastVariant } = {}) =>
    addToast({ variant: opts.variant ?? 'default', message, ...opts }),
  {
    success: variantFn('success'),
    error: variantFn('error'),
    info: variantFn('info'),
    warning: variantFn('warning'),
    message: variantFn('default'),
    loading: (message: string, opts: ToastOptions = {}) =>
      addToast({ variant: 'loading', message, duration: null, ...opts }),
    dismiss: dismissToast,
    update: updateToast,
    promise<T>(
      promise: Promise<T>,
      msgs: {
        loading: string
        success: string | ((value: T) => string)
        error: string | ((err: unknown) => string)
      }
    ): string {
      const id = addToast({
        variant: 'loading',
        message: msgs.loading,
        duration: null,
      })
      promise.then(
        (value) =>
          updateToast(id, {
            variant: 'success',
            message:
              typeof msgs.success === 'function'
                ? msgs.success(value)
                : msgs.success,
            duration: DEFAULT_DURATION,
          }),
        (err) =>
          updateToast(id, {
            variant: 'error',
            message:
              typeof msgs.error === 'function' ? msgs.error(err) : msgs.error,
            duration: DEFAULT_DURATION,
          })
      )
      return id
    },
  }
)

// ── Stack geometry (from the DS2026 spec) ────────────────────────────────────
const VISIBLE_LIMIT = 3 // collapsed toasts shown; rest hide behind a "+N more"
const PEEK = 13 // px each older collapsed toast peeks above the front
const SCALE_STEP = 0.05 // each older toast shrinks by this
const GAP = 10 // px between toasts when expanded
const ROW_H = 56 // first-paint height fallback before measurement
const COLLAPSE_DELAY = 220 // ms grace before re-collapsing on mouse-leave
const WIDTH = 385

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = React.useState(false)
  React.useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    setReduced(mq.matches)
    const on = () => setReduced(mq.matches)
    mq.addEventListener('change', on)
    return () => mq.removeEventListener('change', on)
  }, [])
  return reduced
}

export interface ToasterProps {
  /** Max collapsed toasts before the "+N more" indicator. Default 3. */
  visibleLimit?: number
}

export function Toaster({ visibleLimit = VISIBLE_LIMIT }: ToasterProps = {}) {
  const toasts = React.useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
  const [expanded, setExpanded] = React.useState(false)
  const [heights, setHeights] = React.useState<Record<string, number>>({})
  const reduceMotion = usePrefersReducedMotion()

  const collapseTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  // Auto-dismiss timers, paused while the stack is expanded/hovered.
  const timers = React.useRef<
    Map<string, { remaining: number; startedAt: number; handle: number | null }>
  >(new Map())

  // Newest in front (index 0); older recede behind.
  const stack = React.useMemo(() => [...toasts].reverse(), [toasts])

  // Drive auto-dismiss; pause whenever expanded.
  React.useEffect(() => {
    const map = timers.current
    for (const t of toasts) {
      if (t.duration == null) continue
      const e = map.get(t.id)
      if (expanded) {
        if (e?.handle != null) {
          clearTimeout(e.handle)
          map.set(t.id, {
            remaining: Math.max(0, e.remaining - (Date.now() - e.startedAt)),
            startedAt: 0,
            handle: null,
          })
        }
      } else {
        const remaining = e?.remaining ?? t.duration
        if (!e || e.handle == null) {
          const handle = window.setTimeout(() => dismissToast(t.id), remaining)
          map.set(t.id, { remaining, startedAt: Date.now(), handle })
        }
      }
    }
    // Drop timers for removed toasts.
    for (const id of [...map.keys()]) {
      if (!toasts.some((t) => t.id === id)) {
        const e = map.get(id)
        if (e?.handle != null) clearTimeout(e.handle)
        map.delete(id)
      }
    }
  }, [toasts, expanded])

  React.useEffect(() => {
    const map = timers.current
    const collapse = collapseTimer
    return () => {
      for (const e of map.values()) if (e.handle != null) clearTimeout(e.handle)
      map.clear()
      if (collapse.current) clearTimeout(collapse.current)
    }
  }, [])

  if (stack.length === 0) return null

  const collapse = () => {
    collapseTimer.current = setTimeout(() => setExpanded(false), COLLAPSE_DELAY)
  }
  const expand = () => {
    if (collapseTimer.current) clearTimeout(collapseTimer.current)
    setExpanded(true)
  }

  const overflow = !expanded ? Math.max(0, stack.length - visibleLimit) : 0
  const motion = reduceMotion
    ? ''
    : 'transition-all duration-[450ms] ease-[cubic-bezier(0.22,1,0.36,1)]'

  return (
    <section
      aria-label="Notifications"
      className="pointer-events-none fixed bottom-4 right-4 z-[200] flex justify-end"
      style={{ width: WIDTH, maxWidth: 'calc(100vw - 2rem)' }}
      onMouseEnter={expand}
      onMouseLeave={collapse}
      onFocusCapture={expand}
      onBlurCapture={collapse}
    >
      {overflow > 0 && (
        <span
          className="pointer-events-none absolute right-1 z-10 rounded-full bg-fbui-gray-900 px-2 py-0.5 text-xs font-medium text-fbui-base-white opacity-80"
          style={{
            // Sit just above the collapsed stack (which grows upward from the
            // bottom): front toast height + the peeking back toasts + a gap.
            bottom: (heights[stack[0].id] ?? ROW_H) + visibleLimit * PEEK + 8,
          }}
        >
          +{overflow} more
        </span>
      )}
      <div className="relative w-full">
        {stack.map((t, i) => {
          // Offset of the toasts in front of this one (expanded uses real heights).
          const aheadHeight = stack
            .slice(0, i)
            .reduce((sum, p) => sum + (heights[p.id] ?? ROW_H) + GAP, 0)
          const translateY = expanded ? -aheadHeight : -(i * PEEK)
          const scale = expanded ? 1 : 1 - i * SCALE_STEP
          const hidden = !expanded && i >= visibleLimit
          return (
            <div
              key={t.id}
              ref={(el) => {
                if (!el) return
                // Measure once on mount (single-row toasts don't reflow);
                // feeds the expanded-stack spacing.
                const h = el.offsetHeight
                setHeights((prev) =>
                  prev[t.id] === h ? prev : { ...prev, [t.id]: h }
                )
              }}
              className={cn(
                'pointer-events-auto absolute bottom-0 right-0 w-full origin-bottom',
                motion
              )}
              style={{
                transform: `translateY(${translateY}px) scale(${scale})`,
                zIndex: stack.length - i,
                opacity: hidden ? 0 : 1,
              }}
            >
              <Toast
                variant={t.variant}
                size="sm"
                message={t.message}
                action={t.action}
                onClose={() => dismissToast(t.id)}
                className="w-full"
              />
            </div>
          )
        })}
      </div>
    </section>
  )
}
